import type { Language } from "@heredajo/schema";
import { useEffect, useState } from "react";
import { t } from "../lib/strings.js";

interface Props {
  pageNumber: number;
  pageCount: number;
  slug: string;
  onJump: (page: number) => void;
  lang: Language;
}

export function PageNav({ pageNumber, pageCount, slug, onJump, lang }: Props): JSX.Element {
  const s = t(lang);
  const [draft, setDraft] = useState(pageNumber.toString());

  useEffect(() => {
    setDraft(pageNumber.toString());
  }, [pageNumber]);

  const submitJump = (e: React.FormEvent) => {
    e.preventDefault();
    const n = Number.parseInt(draft, 10);
    if (Number.isFinite(n) && n >= 1 && n <= pageCount) onJump(n);
  };

  return (
    <div className="flex items-center justify-between gap-3 text-sm bg-white border border-parchment-200 rounded-md px-3 py-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onJump(Math.max(1, pageNumber - 1))}
          disabled={pageNumber <= 1}
          className="px-3 py-1 rounded bg-parchment-100 hover:bg-parchment-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ‹ {s.prev}
        </button>
        <button
          type="button"
          onClick={() => onJump(Math.min(pageCount, pageNumber + 1))}
          disabled={pageNumber >= pageCount}
          className="px-3 py-1 rounded bg-parchment-100 hover:bg-parchment-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {s.next} ›
        </button>
      </div>
      <form onSubmit={submitJump} className="flex items-center gap-2">
        <span className="text-ink-muted">{s.pageLocator(slug)}</span>
        <input
          type="number"
          min={1}
          max={pageCount}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-16 px-2 py-1 border border-parchment-200 rounded text-center"
        />
        <span className="text-ink-muted">{s.pageOf(pageCount)}</span>
        <button
          type="submit"
          className="px-2 py-1 rounded bg-ink text-parchment-100 hover:bg-ink-muted text-xs"
        >
          {s.go}
        </button>
      </form>
    </div>
  );
}
